import { FieldArray, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { PrimaryButton } from '@components/Buttons/PrimaryButtons';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import {
  CancelButton,
  DeleteButton,
  FormikDateInput,
  SubmitButton,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { Loading } from '@components/Loading';
import { Space } from '@components/Space';
import { useNotification } from '@hooks/useNotification';
import {
  SalesTaxViewModel,
  salesTaxDefaultValue,
  salesTaxValidation,
} from '@models/SalesTax';
import { SalesTaxService } from '@services/SalesTax';

interface Props {
  taxId?: number;
  onClose?: (refreshGrid?: boolean) => void;
  onDelete?: () => void;
}

export const SalesTaxEdit: React.FC<Props> = ({ taxId, onClose, onDelete }) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [salesTax, setSalesTax] = useState(salesTaxDefaultValue);
  const { showError, showSuccess } = useNotification();

  const loadData = async () => {
    const result = await SalesTaxService.getById(Number(taxId));

    if (result.isSuccess && 'data' in result) {
      loadSalesTaxRates(result.data);
      console.log('Success: Fetched the record.');
    } else {
      console.error('Error: Failed to get record.', result.errorMessages);
      showError(`${result.errorMessages}`);
      onClose && onClose();
    }
  };

  const loadSalesTaxRates = async (salesTax: SalesTaxViewModel) => {
    const result = await SalesTaxService.getSalesTaxRates(Number(taxId));
    if (result.isSuccess && 'data' in result) {
      salesTax.salesTaxRates = result.data;
      setSalesTax(salesTax);
      setIsLoading(false);
      console.log('Success: Fetched the record.');
    } else {
      console.error('Error: Failed to get record.', result.errorMessages);
      showError(`${result.errorMessages}`);
      onClose && onClose();
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  if (isLoading) {
    return <Loading />;
  } else {
    return (
      <>
        <CloseButton onClick={onClose} />

        <Box size="medium">
          <Formik
            initialValues={salesTax}
            validationSchema={salesTaxValidation}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values, actions) => {
              SalesTaxService.update(Number(taxId), values)
                .then((result) => {
                  if (result.isSuccess) {
                    showSuccess('Successfully updated the record.');
                    onClose && onClose(true);
                  } else {
                    console.error(
                      'Error: Failed to update record.',
                      result.errorMessages
                    );
                    showError(`${result.errorMessages}`);
                  }
                })
                .finally(() => {
                  actions.setSubmitting(false);
                });
            }}
          >
            {(formikProps) => {
              return (
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    <FormikInput label="Name" name="name" />
                    <FormikInput label="Description" name="description" />
                    <FormikInput label="Abbreviation" name="abbreviation" />
                    <FieldArray
                      name="salesTaxRates"
                      render={(arrayHelpers) => (
                        <div className="flex flex-col space-y-4 content-start">
                          {formikProps.values.salesTaxRates &&
                            formikProps.values.salesTaxRates.map(
                              (rate, index) => (
                                <div
                                  key={index}
                                  className="flex flex-row items-start space-x-1.5"
                                >
                                  <FormikInput
                                    label="Rate (%)"
                                    name={`salesTaxRates[${index}].rate`}
                                    value={rate.rate}
                                  />
                                  <FormikDateInput
                                    label="Effective Date"
                                    name={`salesTaxRates[${index}].effectiveDate`}
                                    value={rate.effectiveDate}
                                  />

                                  <div className="pt-6">
                                    <PrimaryButton
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      -
                                    </PrimaryButton>
                                  </div>
                                </div>
                              )
                            )}

                          <PrimaryButton
                            onClick={() =>
                              arrayHelpers.push({
                                id: 0,
                                rate: '',
                                effectiveDate: '',
                                salesTaxId: taxId,
                              })
                            }
                          >
                            + Rate
                          </PrimaryButton>
                        </div>
                      )}
                    />
                    <Space />
                  </FormSectionContainer>
                  <FormButtonsContainer>
                    <div className="flex space-x-4">
                      <CancelButton onClick={onClose} />

                      <DeleteButton onClick={onDelete} />

                      <SubmitButton
                        label="Save"
                        disabled={
                          formikProps.isSubmitting || !formikProps.isValid
                        }
                      />
                    </div>
                  </FormButtonsContainer>
                </form>
              );
            }}
          </Formik>
        </Box>
      </>
    );
  }
};
